/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment as QAENV } from '@environments/environment.qa';
import { environment as DEVENV } from '@environments/environment.demo';
import { environment as PRODENV } from '@environments/environment.prod';
import { environment as STAGINGENV } from '@environments/environment.staging';
import {
  randCompanyName,
  randVehicleManufacturer,
  randFirstName,
  randLastName,
  randCity,
  randAlphaNumeric
} from '@ngneat/falso';
import { ViewableAsset } from '@app/modules/location/models/viewable-asset.model';
import { DriverProfile } from '@app/modules/driver/models/driver.models';
import { Division, DriverList, VehicleList, VehicleListItem } from '@app/core/models/zonar-accounts.model';
import { Environment } from '@environments/shared.models';

const UNIQUE_HOME_LOCATION_ID = 'uniqueHomeLocation';
@Injectable()
export class AnonymizedDataResponseInterceptor implements HttpInterceptor {
  assetRoute: string;
  driverAssetRoute: string;
  vehiclesRoute: string;
  driversRoute: string;
  vehicleLocationRoute: string;
  sitesRoute: string;
  pathRoute: string;
  driverVehicleAssociationRoute: string;
  vdoProfileRoute: string;
  assetMakesMap: Map<string, string> = new Map<string, string>();
  assetNamesMap: Map<string, string> = new Map<string, string>();
  assetVINsMap: Map<string, string> = new Map<string, string>();
  assetLocationsMap: Map<string, string> = new Map<string, string>();
  locationsMap: Map<string, string> = new Map<string, string>();
  companyName: string;
  divisionName: string;
  driverNamesMap: Map<string, Array<string>> = new Map<string, Array<string>>();
  isAnonymousDataEnabled: boolean;

  constructor() {
    const storedAssetsVinMap = sessionStorage.getItem('assetVINsMap');
    this.assetVINsMap = storedAssetsVinMap ? new Map(JSON.parse(storedAssetsVinMap)) : this.assetVINsMap;
    switch (window.location.origin) {
      case DEVENV.appUrl:
        this.setEnvironmentVariables(DEVENV);
        break;
      case QAENV.appUrl:
        this.setEnvironmentVariables(QAENV);
        break;
      case PRODENV.appUrl:
        this.setEnvironmentVariables(PRODENV);
        break;
      case STAGINGENV.appUrl:
        this.setEnvironmentVariables(STAGINGENV);
        break;
      default:
        this.setEnvironmentVariables(DEVENV);
        break;
    }
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Destructuring of request
    const { url, method } = request;
    this.isAnonymousDataEnabled = false;
    if (sessionStorage.getItem('demoMode')) {
      this.isAnonymousDataEnabled = sessionStorage.getItem('demoMode') === 'true';
    }
    // Case handler
    if (this.isAnonymousDataEnabled) {
      switch (this.isAnonymousDataEnabled) {
        // case url.includes(this.assetRoute): {
        //   return this.handleAssetRoute(request, next);
        // }
        case url.includes(this.vehiclesRoute): {
          return this.handleVehiclesRoute(request, next);
        }
        case url.includes(this.driversRoute): {
          return this.handleDriversRoute(request, next);
        }

        case url.includes(this.vehicleLocationRoute): {
          return this.handleVehicleLocationRoute(request, next);
        }

        case url.includes(this.sitesRoute): {
          return this.handleSitesRoute(request, next);
        }

        case url.includes(this.pathRoute): {
          return this.handlePathRoute(request, next);
        }

        case url.includes(this.driverVehicleAssociationRoute): {
          return this.handleDriverVehicleAssociationRoute(request, next);
        }
        case url.includes(this.vdoProfileRoute): {
          return this.handleVdoProfileRoute(request, next);
        }
        default: {
          // Omit other petitions
          return next.handle(request).pipe(response => response);
        }
      }
    }
    return next.handle(request).pipe(response => response);
  }

  setEnvironmentVariables(env: Environment) {
    this.assetRoute = env.locationApi.url.concat('/assets');
    this.vehiclesRoute = `${env.vdofleet.baseApiUrl}/master-data-service/v1/vehicles`;
    this.driversRoute = `${env.vdofleet.baseApiUrl}/master-data-service/v1/drivers`;
    this.vehicleLocationRoute = `${env.vdofleet.baseApiUrl}/historical-asset-service/api/v1/vehicle_location`;
    this.sitesRoute = `${env.vdofleet.baseApiUrl}/master-data-service/v2/sites/all`;
    this.pathRoute = `${env.vdofleet.baseApiUrl}/historical-asset-service/api/v1/path`;
    this.driverVehicleAssociationRoute = `${env.vdofleet.baseApiUrl}/historical-asset-service/api/v1/vehicle_driver_association`;
    this.driverAssetRoute = env.locationApi.url.concat('/vehicle_driver_association');
    this.vdoProfileRoute = env.vdofleet.baseApiUrl.concat('/id/v2/users/current');
  }

  getMockAssetName(assetId: string): string {
    if (!this.assetNamesMap.has(assetId)) {
      this.assetNamesMap.set(assetId, randAlphaNumeric({ length: 10 }).join(''));
    }
    return this.assetNamesMap.get(assetId);
  }

  getMockDriverName(driverId: string): Array<string> {
    if (!this.driverNamesMap.has(driverId)) {
      this.driverNamesMap.set(driverId, [randFirstName(), randLastName()]);
    }
    return this.driverNamesMap.get(driverId);
  }

  getMockCompanyName(): string {
    if (!this.companyName) {
      this.companyName = `DEMO ${randCompanyName()}`;
    }
    return this.companyName;
  }

  getMockSiteName(): string {
    return randCompanyName();
  }

  getMockDivisionName(locationId: string): string {
    if (!this.locationsMap.has(locationId)) {
      this.assetLocationsMap.set(locationId, randCity());
    }
    return this.assetLocationsMap.get(locationId);
  }

  getMockAssetVIN(assetId: string): string {
    if (!this.assetVINsMap.has(assetId)) {
      this.assetVINsMap.set(assetId, randAlphaNumeric({ length: 17 }).join(''));
      sessionStorage.setItem('assetVINsMap', JSON.stringify(Array.from(this.assetVINsMap.entries())));
    }
    return this.assetVINsMap.get(assetId);
  }

  getVinFromMock(fakeVin: string): string {
    for (const [key, value] of this.assetVINsMap.entries()) {
      if (fakeVin === value) {
        return key;
      }
    }
    return null;
  }

  // getMockAssetMake(assetId: string): string {
  //   if (!this.assetMakesMap.has(assetId)) {
  //     this.assetMakesMap.set(assetId, randVehicleManufacturer());
  //   }
  //   return this.assetMakesMap.get(assetId);
  // }

  // handleAssetRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  //   return next.handle(request).pipe(
  //     map((response: any) => {
  //       if (response.body) {
  //         const data = response.body;
  //         if (request.method == 'POST') {
  //           const assets: Array<ViewableAsset> = data.assets;
  //           for (const asset of assets) {
  //             asset.assetName = this.getMockAssetName(asset.assetId);
  //             const driverName = this.getMockDriverName(asset.driverProfileId);
  //             asset.driverFirstName = driverName[0];
  //             asset.driverLastName = driverName[1];
  //             asset.legacyAccount = this.getMockCompanyName();
  //             // asset.homeLocationId = UNIQUE_HOME_LOCATION_ID;
  //           }
  //         }
  //         if (request.method == 'GET') {
  //           const asset: ViewableAsset = data;
  //           asset.assetName = this.getMockAssetName(asset.assetId);
  //           asset.assetTypeInfo.licenseNumber = this.getMockAssetName(asset.assetId);
  //           const driverName = this.getMockDriverName(asset.driverProfileId);
  //           asset.driverFirstName = driverName[0];
  //           asset.driverLastName = driverName[1];
  //           asset.legacyAccount = this.getMockCompanyName();
  //           asset.divisionId = UNIQUE_HOME_LOCATION_ID;
  //           // asset.homeLocationId = UNIQUE_HOME_LOCATION_ID;
  //           asset.assetTypeInfo.vin = this.getMockAssetVIN(asset.assetId);
  //           asset.make = this.getMockAssetMake(asset.assetId);
  //         }
  //         return response;
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }

  // handleCompanyRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  //   return next.handle(request).pipe(
  //     map((response: any) => {
  //       if (request.url.includes('/divisions')) {
  //         if (response.body) {
  //           const data = response.body;
  //           if (request.method == 'GET') {
  //             const divisions: Array<Division> = data;
  //             for (const division of divisions) {
  //               division.name = this.getMockDivisionName(UNIQUE_HOME_LOCATION_ID);
  //               division.locationId = UNIQUE_HOME_LOCATION_ID;
  //             }
  //           }
  //           return response;
  //         } else {
  //           return response;
  //         }
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }

  // handleDriverProfileRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  //   return next.handle(request).pipe(
  //     map((response: any) => {
  //       if (response.body) {
  //         const data = response.body;
  //         if (request.method == 'GET') {
  //           const driver: DriverProfile = data;
  //           const driverName = this.getMockDriverName(driver.id);
  //           driver.legacyFirstName = driverName[0];
  //           driver.legacyLastName = driverName[1];
  //         }
  //         return response;
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }

  handleVehiclesRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map((response: any) => {
        if (response.body) {
          const data: VehicleList = response.body;
          if (request.method == 'GET') {
            for (const vehicle of data.data) {
              const assetName = this.getMockAssetName('' + vehicle.id);
              vehicle.displayName = assetName;
              vehicle.vrn = assetName;
              vehicle.label = assetName;
              vehicle.vin = this.getMockAssetVIN(vehicle.vin);
            }
          }
          return response;
        } else {
          return response;
        }
      })
    );
  }

  handleDriversRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = request.urlWithParams;
    if (url === this.driversRoute || url.startsWith(this.driversRoute + '?')) {
      const urlObj = new URL(url);
      const hasDriverIdentification = urlObj.searchParams.has('driverIdentification');
      if (!hasDriverIdentification) {
        return next.handle(request).pipe(
          map((response: any) => {
            if (response.body && request.method === 'GET') {
              const data: DriverList = response.body;
              for (const driver of data.data) {
                const [firstName, lastName] = this.getMockDriverName(String(driver.id));
                driver.firstName = firstName;
                driver.lastName = lastName;
              }
            }
            return response;
          })
        );
      } else {
        return next.handle(request);
      }
    }
    return next.handle(request);
  }

  handleSitesRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      map((response: any) => {
        if (response.body && request.method === 'GET' && Array.isArray(response.body)) {
          for (const site of response.body) {
            site.name = this.getMockSiteName();
          }
        }
        return response;
      })
    );
  }

  handlePathRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes(this.pathRoute)) {
      const modifiedReq = this.updateRequestUrlWithVin(request);
      return next.handle(modifiedReq).pipe(
        map((response: any) => {
          if (response.body) {
            const data = response.body;
            data.summary.vin = this.getVinFromMock(data.summary.vin);
          }
          return response;
        })
      );
    }
    return next.handle(request);
  }

  handleDriverVehicleAssociationRoute(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const mockedVin = request.params.get('vin');
    const updatedParams = request.params.set('vin', this.getVinFromMock(mockedVin));
    const modifiedReq = request.clone({ params: updatedParams });

    return next.handle(modifiedReq).pipe(
      map((response: any) => {
        if (response.body) {
          response.body.vin = this.getVinFromMock(response.body.vin);
        }
        return response;
      })
    );
  }

  handleVehicleLocationRoute(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const regexWithVehicleId = /\/vehicle_location\/[^?]+/;
    const isVehicleLocationRoute = request.url.startsWith(this.vehicleLocationRoute);
    const hasPathParam = request.url.includes('/vehicle_location') && regexWithVehicleId.test(request.url);
    if (!isVehicleLocationRoute) {
      return next.handle(request);
    }

    let modifiedRequest;

    if (!hasPathParam) {
      const modifiedPayload = {
        ...request.body,
        vins: request.body.vins.map(vin => this.getVinFromMock(vin))
      };
      modifiedRequest = request.clone({ body: modifiedPayload });
    } else {
      modifiedRequest = this.updateRequestUrlWithVin(request);
    }

    return next.handle(modifiedRequest).pipe(
      map((response: any) => {
        if (!response.body) return response;

        if (!hasPathParam) {
          for (const vehicle of response.body) {
            vehicle.vin = this.getMockAssetVIN(vehicle.vin);
            vehicle.id = this.getMockAssetVIN(vehicle.id);
          }
        } else {
          const data = response.body;
          data.id = this.getMockAssetVIN(data.id);
          data.vin = this.getMockAssetVIN(data.vin);
          data.deviceId = randAlphaNumeric({ length: 15 }).join('');
          data.odometer = this.getRandomInt(0, 300000);
        }
        return response;
      })
    );
  }

  getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  handleVdoProfileRoute(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      map((response: any) => {
        if (response.body) {
          const data = response.body;
          if (request.method === 'GET') {
            data.account.displayName = this.getMockCompanyName();
          }
          return response;
        } else {
          return response;
        }
      })
    );
  }
  updateRequestUrlWithVin(request: HttpRequest<any>): HttpRequest<any> {
    const parts = request.url.split('/');
    const mockedVehileId = parts[parts.length - 1].split('?')[0];
    const vehicleId = this.getVinFromMock(mockedVehileId);
    const modifiedUrl = request.url.replace(mockedVehileId, vehicleId);
    const modifiedRequest = request.clone({ url: modifiedUrl });
    return modifiedRequest;
  }
  // handleDriverAssetRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  //   return next.handle(request).pipe(
  //     map((response: any) => {
  //       if (response.body) {
  //         const data = response.body;
  //         if (request.method == 'GET') {
  //           const driverAssetsProfiles: Array<DriverAsset> = data;
  //           for (let i = 0; i < driverAssetsProfiles.length; i++) {
  //             // eslint-disable-next-line security/detect-object-injection
  //             const driverProfile: DriverProfile = driverAssetsProfiles[i].driverProfile;
  //             const driverName = this.getMockDriverName(driverProfile.id);
  //             driverProfile.legacyFirstName = driverName[0];
  //             driverProfile.legacyLastName = driverName[1];
  //           }
  //         }
  //         return response;
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }

  // handleSettingRoute(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
  //   return next.handle(request).pipe(
  //     map((response: any) => {
  //       if (response.body) {
  //         const data = response.body;
  //         if (request.method == 'GET') {
  //           const setting: SettingInfo = data[0];
  //           setting.value = setting.value.replace(/title:.*,/, `title:${this.getMockCompanyName()},`);
  //         }
  //         return response;
  //       } else {
  //         return response;
  //       }
  //     })
  //   );
  // }
}
// Facilitate import from app.module, contains this Interceptor
export const AnonymizedDataResponseProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: AnonymizedDataResponseInterceptor,
  multi: true
};
